import { HardHat, ShieldCheck, Truck, Zap, Wrench, Users } from "lucide-react";

const img = (id) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=1200&q=80`;

export const services = [
  ["01", "Civil Construction Works", "Project sites, concreting and structural works.", HardHat],
  ["02", "Sewage & Drainage", "Drainage cleaning, sewage and site utility works.", ShieldCheck],
  ["03", "Vacuum Tanker Operations", "Vacuum tanker and septic tank cleaning services.", Truck],
  ["04", "Cable Laying & Utilities", "Trenching and underground cabling for infrastructure.", Zap],
  ["05", "Plumbing Pipeline Works", "Reliable industrial and domestic pipeline execution.", Wrench],
  ["06", "Electrical Fittings", "Industrial and domestic electrical fitting support.", Zap],
  ["07", "Manpower Supply", "Skilled and semi-skilled teams, ready to mobilise.", Users],
  ["08", "Sub-Contract Execution", "Dependable delivery for industrial project partners.", ShieldCheck],
];

export const capabilities = [
  { num: "01", title: "Civil Construction Works", icon: HardHat, photo: img("1541888946425-d81bb19240f5"),
    desc: "Professional civil construction and site execution supported by skilled manpower, project coordination and safety-focused site practices.",
    activities: ["Project site works", "Site preparation", "Concreting works", "Structural works", "Civil construction activities", "General construction support", "Skilled workforce deployment", "Site supervision"] },
  { num: "02", title: "Sewage & Drainage Services", icon: ShieldCheck, photo: img("1513828583688-c52646db42da"),
    desc: "Reliable sewage and drainage support services for industrial and project sites, including cleaning, maintenance and related site operations.",
    activities: ["Sewage works", "Drainage systems", "Drainage cleaning", "Site drainage support", "Underground drainage-related works", "Cleaning and maintenance activities", "Skilled manpower deployment"] },
  { num: "03", title: "Vacuum Tanker / Septic Tank Cleaning", icon: Truck, photo: img("1601584115197-04ecc0da31d7"),
    desc: "Vacuum tanker and septic tank cleaning services executed with safe handling and safety-focused site practices.",
    activities: ["Vacuum tanker operations", "Septic tank cleaning", "Sewage handling", "Tank cleaning operations", "Drainage cleaning support", "Site safety and PPE"] },
  { num: "04", title: "Cable Laying & Underground Utility Works", icon: Zap, photo: img("1473341304170-971dccb5ac1e"),
    desc: "Trenching and underground cabling for infrastructure, coordinated with site teams and project requirements.",
    activities: ["Cable trenching", "Underground cable laying", "Utility trench preparation", "Cable route execution", "Underground utility support", "Site coordination", "Skilled workforce deployment"] },
  { num: "05", title: "Plumbing & Pipeline Works", icon: Wrench, photo: img("1607472586893-edb57bdc0e39"), highlight: ["24", "Plumbers"],
    desc: "Reliable industrial and domestic pipeline execution by skilled plumbing teams.",
    activities: ["Plumbing installation", "Pipeline installation", "Industrial plumbing", "Domestic plumbing", "Pipeline support works", "Maintenance and site execution", "Skilled plumber deployment"] },
  { num: "06", title: "Industrial & Domestic Electrical Fittings", icon: Zap, photo: img("1621905251189-08b45d6a269e"), highlight: ["20", "Electricians"],
    desc: "Industrial and domestic electrical fitting support with safety-focused execution.",
    activities: ["Electrical fittings", "Industrial electrical works", "Domestic electrical works", "Electrical installation", "Site electrical support", "Skilled electrician deployment", "Safety-focused execution"] },
  { num: "07", title: "Skilled & Semi-Skilled Manpower Supply", icon: Users, photo: img("1504307651254-35680f356dfd"), highlight: ["200+", "Scalable workforce"],
    desc: "Skilled and semi-skilled teams, ready to mobilise according to project requirements.",
    activities: ["Skilled manpower", "Semi-skilled manpower", "Workforce mobilization", "Site deployment", "Workforce coordination", "Project-based manpower requirements", "Scalable workforce capability"] },
  { num: "08", title: "Sub-Contract Execution for Industrial Projects", icon: ShieldCheck, photo: "https://customer-assets-eiarnc6j.emergentagent.net/job_maintain-track-8/artifacts/ympkcfj0_wallpaperflare.com_wallpaper.jpg",
    desc: "Dependable delivery for industrial project partners with coordinated site execution.",
    activities: ["Industrial project execution", "Site workforce deployment", "Project coordination", "Construction support", "Utility works", "Skilled manpower management", "Safety and quality-focused execution", "Timely project delivery"] },
];

export const serviceDetails = {
  "01": {
    tagline: "Building strong foundations for every project, from groundbreaking to handover.",
    overview: [
      "Our Civil Construction Works division delivers end-to-end site execution for industrial, commercial, and infrastructure projects. Backed by a skilled and experienced workforce, coordinated project management, and a strict focus on safety, we handle everything from initial site preparation to structural completion — ensuring every phase is executed on schedule and to specification.",
      "Whether you're breaking ground on a new facility or need reliable manpower to support an ongoing build, our teams integrate seamlessly with your project timelines and site requirements.",
    ],
    offer: [
      ["Project Site Works", "Full-scale coordination of on-site construction activities from mobilization to completion"],
      ["Concreting Works", "Precision concrete pouring, finishing, and curing for slabs, foundations, and structural elements"],
      ["Civil Construction Activities", "General civil works across residential, commercial, and industrial sites"],
      ["Skilled Workforce Deployment", "Trained masons, steel fixers, formwork carpenters, and general labour supplied per project scale"],
      ["Site Preparation", "Land clearing, leveling, excavation, and groundwork readiness"],
      ["Structural Works", "Column, beam, slab, and framework construction to engineering specifications"],
      ["General Construction Support", "Auxiliary site services including material handling and site logistics"],
      ["Site Supervision", "Dedicated supervisors overseeing quality, safety, and progress at every stage"],
    ],
    process: [
      ["Site Assessment", "We evaluate the project scope, site conditions, and manpower/equipment needs"],
      ["Planning & Mobilization", "Workforce and resources are deployed according to the agreed project schedule"],
      ["Execution", "Construction activities are carried out under continuous on-site supervision"],
      ["Quality & Safety Checks", "Regular inspections ensure work meets safety and quality benchmarks"],
      ["Handover", "Completed work is reviewed and handed over per client requirements"],
    ],
    why: [
      "Experienced, safety-trained construction workforce",
      "Dedicated site supervisors for quality control",
      "Flexible manpower scaling based on project size",
      "Transparent coordination with project stakeholders",
      "Proven track record across industrial and civil sites",
    ],
  },
  "02": {
    tagline: "Reliable drainage and sewage support, built for industrial and project sites.",
    overview: [
      "Our Sewage & Drainage Services team handles the full range of drainage-related site operations — from underground drainage installation to ongoing cleaning and maintenance. We support industrial facilities and active project sites with dependable, safety-conscious crews trained to work in demanding environments.",
    ],
    offer: [
      ["Sewage Works", "Installation and upkeep of sewage systems for industrial and project sites"],
      ["Drainage Systems", "Design-aligned drainage network construction and repair"],
      ["Drainage Cleaning", "Routine and emergency drain clearing to prevent blockages and overflow"],
      ["Site Drainage Support", "On-site drainage management during active construction phases"],
      ["Underground Drainage-Related Works", "Excavation and installation of underground drainage infrastructure"],
      ["Cleaning and Maintenance Activities", "Scheduled maintenance to keep systems running efficiently"],
      ["Skilled Manpower Deployment", "Trained crews equipped for sewage and drainage operations"],
    ],
    process: [
      ["Site Inspection", "Assess existing drainage/sewage infrastructure and site needs"],
      ["Planning", "Determine the right crew size, equipment, and approach"],
      ["Execution", "Carry out installation, cleaning, or repair works on-site"],
      ["Testing & Verification", "Confirm systems are functioning correctly"],
      ["Ongoing Maintenance", "Scheduled follow-up service where required"],
    ],
    why: [
      "Experienced drainage and sewage crews",
      "Fast response for cleaning and maintenance needs",
      "Safety-focused site practices",
      "Reliable support for both new installations and ongoing upkeep",
    ],
  },
  "03": {
    tagline: "Safe, efficient vacuum tanker and septic tank cleaning services.",
    overview: [
      "We provide professional vacuum tanker and septic tank cleaning services designed for industrial sites, commercial facilities, and residential needs. Our operators follow strict safety and handling protocols to ensure clean, efficient service with minimal disruption to your site.",
    ],
    offer: [
      ["Vacuum Tanker Operations", "Fully equipped tankers for waste extraction and transport"],
      ["Sewage Handling", "Safe collection and disposal of sewage waste"],
      ["Drainage Cleaning Support", "Complementary drainage clearing during tanker operations"],
      ["Septic Tank Cleaning", "Complete septic tank pump-out and cleaning service"],
      ["Tank Cleaning Operations", "Cleaning of storage and holding tanks across facility types"],
      ["Site Safety and PPE", "Full protective equipment and safety protocols followed on every job"],
    ],
    process: [
      ["Assessment", "Evaluate tank size, waste type, and site access"],
      ["Scheduling", "Arrange a convenient service time with minimal site disruption"],
      ["Extraction", "Vacuum tanker safely removes waste"],
      ["Disposal", "Waste is transported and disposed of per regulations"],
      ["Site Cleanup", "Work area left clean and safe"],
    ],
    why: [
      "Trained operators with strict safety compliance",
      "Modern vacuum tanker equipment",
      "Prompt, reliable scheduling",
      "Proper waste handling and disposal",
    ],
  },
  "04": {
    tagline: "Trenching and underground cabling, coordinated with your project timeline.",
    overview: [
      "Our Cable Laying & Underground Utility Works team handles trenching and underground cable installation for infrastructure projects of all sizes. We coordinate closely with site teams and engineering requirements to ensure utilities are laid safely, accurately, and on schedule.",
    ],
    offer: [
      ["Cable Trenching", "Precision trenching for cable routes"],
      ["Underground Cable Laying", "Safe installation of underground cabling"],
      ["Utility Trench Preparation", "Site prep for utility installation works"],
      ["Cable Route Execution", "Planned execution of cable pathways per project design"],
      ["Underground Utility Support", "General support for underground infrastructure works"],
      ["Site Coordination", "Close coordination with project teams and other contractors"],
      ["Skilled Workforce Deployment", "Trained crews for trenching and cable-laying operations"],
    ],
    process: [
      ["Route Planning", "Align cable paths with project and engineering plans"],
      ["Trenching", "Excavate trenches to required depth and specification"],
      ["Cable Laying", "Install and secure cabling per standards"],
      ["Backfilling & Restoration", "Restore the site surface after installation"],
      ["Coordination & Handover", "Sync with other site teams and confirm completion"],
    ],
    why: [
      "Experienced underground utility crews",
      "Strong coordination with project stakeholders",
      "Efficient trenching and cable-laying execution",
      "Safety-first site practices",
    ],
  },
  "05": {
    tagline: "Reliable industrial and domestic pipeline execution by 24 skilled plumbers.",
    overview: [
      "Our team of 24 skilled plumbers delivers dependable plumbing and pipeline services across industrial and domestic settings. From new installations to maintenance and repair, we ensure every job is completed with quality workmanship and minimal disruption.",
    ],
    offer: [
      ["Plumbing Installation", "New plumbing systems installed to specification"],
      ["Pipeline Installation", "Industrial and domestic pipeline setup"],
      ["Industrial Plumbing", "Specialized plumbing support for industrial facilities"],
      ["Domestic Plumbing", "Residential plumbing installation and repair"],
      ["Pipeline Support Works", "Auxiliary support for pipeline projects"],
      ["Maintenance and Site Execution", "Ongoing maintenance and on-site plumbing execution"],
      ["Skilled Plumber Deployment", "24 trained plumbers available for project deployment"],
    ],
    process: [
      ["Requirement Assessment", "Understand the scope, whether industrial or domestic"],
      ["Planning", "Determine materials, layout, and manpower needed"],
      ["Installation/Repair", "Execute plumbing or pipeline work"],
      ["Testing", "Verify systems for leaks and proper function"],
      ["Maintenance Follow-Up", "Ongoing support as needed"],
    ],
    why: [
      "Team of 24 experienced plumbers",
      "Capability across industrial and domestic projects",
      "Quality-focused installation and repair",
      "Reliable maintenance and site execution",
    ],
  },
  "06": {
    tagline: "Safety-focused electrical fitting support by 20 skilled electricians.",
    overview: [
      "Our team of 20 skilled electricians provides industrial and domestic electrical fitting services, ensuring every installation meets safety standards and functions reliably. From site electrical support to full installations, we bring trained expertise to every job.",
    ],
    offer: [
      ["Electrical Fittings", "Installation of fittings for industrial and residential use"],
      ["Industrial Electrical Works", "Electrical support tailored to industrial facilities"],
      ["Domestic Electrical Works", "Residential electrical installation and repair"],
      ["Electrical Installation", "Full electrical system setup per project needs"],
      ["Site Electrical Support", "On-site electrical assistance during construction"],
      ["Skilled Electrician Deployment", "20 trained electricians available for deployment"],
      ["Safety-Focused Execution", "Strict adherence to electrical safety protocols"],
    ],
    process: [
      ["Site Evaluation", "Assess electrical requirements for the project"],
      ["Planning", "Design the fitting and installation approach"],
      ["Installation", "Execute electrical work per safety standards"],
      ["Testing & Inspection", "Verify all installations are safe and functional"],
      ["Handover", "Confirm completion and provide any necessary documentation"],
    ],
    why: [
      "Team of 20 qualified electricians",
      "Strong safety compliance on every job",
      "Experience across industrial and domestic settings",
      "Dependable, timely execution",
    ],
  },
  "07": {
    tagline: "A scalable workforce of 200+ ready to mobilise for your project.",
    overview: [
      "We provide skilled and semi-skilled manpower supply for construction and industrial projects of any scale. With a pool of over 200 workers, we can mobilize the right team quickly, matched to your specific project requirements and timeline.",
    ],
    offer: [
      ["Skilled Manpower", "Trained tradespeople for specialized project tasks"],
      ["Semi-Skilled Manpower", "Supporting workforce for general site operations"],
      ["Workforce Mobilization", "Rapid deployment of teams as project demands grow"],
      ["Site Deployment", "Manpower placed directly on-site per project needs"],
      ["Workforce Coordination", "Management of teams to align with project schedules"],
      ["Project-Based Manpower Requirements", "Flexible staffing tailored to each project's scope"],
      ["Scalable Workforce Capability", "Ability to scale up or down as project phases change"],
    ],
    process: [
      ["Requirement Review", "Understand project scale and skill requirements"],
      ["Team Selection", "Match the right skilled/semi-skilled workers to the job"],
      ["Mobilization", "Deploy workforce to site on schedule"],
      ["On-Site Coordination", "Manage day-to-day workforce needs"],
      ["Scaling", "Adjust team size as the project progresses"],
    ],
    why: [
      "200+ scalable workforce capacity",
      "Quick mobilization for urgent project needs",
      "Mix of skilled and semi-skilled labour available",
      "Reliable coordination and on-site management",
    ],
  },
  "08": {
    tagline: "Dependable, coordinated delivery for industrial project partners.",
    overview: [
      "We take on sub-contract execution for industrial projects, delivering coordinated site work backed by skilled manpower and strong project management. Our focus is dependable delivery — on time, on budget, and to the required quality and safety standards.",
    ],
    offer: [
      ["Industrial Project Execution", "End-to-end delivery of sub-contracted industrial work"],
      ["Project Coordination", "Seamless integration with main contractors and project teams"],
      ["Utility Works", "Support for utility-related tasks within larger projects"],
      ["Site Workforce Deployment", "Manpower supplied and managed for sub-contracted scopes"],
      ["Construction Support", "General construction assistance as part of larger project execution"],
      ["Skilled Manpower Management", "Oversight of skilled teams throughout the contract"],
      ["Safety and Quality-Focused Execution", "Consistent adherence to safety and quality benchmarks"],
      ["Timely Project Delivery", "Committed to meeting agreed project timelines"],
    ],
    process: [
      ["Scope Alignment", "Understand the sub-contract requirements and deliverables"],
      ["Resource Planning", "Allocate manpower and coordinate with the main project team"],
      ["Execution", "Carry out the sub-contracted work on-site"],
      ["Quality & Safety Monitoring", "Ongoing checks throughout execution"],
      ["Delivery & Handover", "Complete and hand over work per agreed timelines"],
    ],
    why: [
      "Proven experience as a dependable sub-contract partner",
      "Strong coordination with main contractors",
      "Consistent safety and quality standards",
      "Reliable, on-time project delivery",
    ],
  },
};
